import countryData from './countory.json';

export const COUNTRY_PHONE_LIST = countryData?.countoryCode || [];

function stripEnvQuotes(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/^['"]|['"]$/g, '').trim();
}

/** Match `users.country` / env country label to a row in countory.json (countryName or "Country (+code)" label). */
function findDialCodeRowByCountryLabel(label) {
  if (!label || typeof label !== 'string') return null;
  const lower = String(label).trim().toLowerCase();
  if (!lower) return null;
  let row = COUNTRY_PHONE_LIST.find((c) => (c.countryName || '').toLowerCase() === lower);
  if (!row) {
    row = COUNTRY_PHONE_LIST.find(
      (c) =>
        (c.country || '').toLowerCase() === lower ||
        (c.country || '').toLowerCase().startsWith(`${lower} (`),
    );
  }
  return row || null;
}

/** Dial code string (e.g. "+91") from a stored profile country name, or null if unknown. */
export function getDialCodeFromUserCountry(country) {
  return findDialCodeRowByCountryLabel(country)?.code ?? null;
}

/** Ensure dial codes display with a leading + when stored as digits only (e.g. "91" → "+91"). */
export function normalizeDialCodeForDisplay(code) {
  const s = String(code || '').trim();
  if (!s) return '';
  if (s.startsWith('+')) return s;
  if (/^\d{1,4}$/.test(s)) return `+${s}`;
  return s;
}

/**
 * Default dial code for phone fields (register, create modals, profile before API load).
 * Uses only REACT_APP_DEFAULT_PHONE_COUNTRY (country name as in countory.json). Falls back to +91.
 */
export function getDefaultPhoneCountryCode() {
  const label = stripEnvQuotes(process.env.REACT_APP_DEFAULT_PHONE_COUNTRY);
  return findDialCodeRowByCountryLabel(label)?.code || '+91';
}

export function getFallbackPhoneRule() {
  return { min: 5, max: 15, allowedLengths: null };
}

export function getCountryByCode(countryCode) {
  const code = String(countryCode || '').trim();
  return COUNTRY_PHONE_LIST.find((country) => country.code === code) || null;
}

/** "India (+91)" → "India" for picker rows (code shown separately). */
export function getCountryDisplayName(entry) {
  if (!entry?.country) return '';
  const idx = entry.country.lastIndexOf(' (');
  return idx >= 0 ? entry.country.slice(0, idx).trim() : entry.country.trim();
}

export function getPhoneRuleFromCountry(country) {
  if (!country) return getFallbackPhoneRule();

  const allowedLengths = Array.isArray(country.allowedLengths)
    ? country.allowedLengths.filter((n) => Number.isInteger(n) && n > 0)
    : null;

  const minLength = Number.isInteger(country.minLength) && country.minLength > 0
    ? country.minLength
    : null;
  const maxLength = Number.isInteger(country.maxLength) && country.maxLength > 0
    ? country.maxLength
    : null;

  if (allowedLengths && allowedLengths.length > 0) {
    return {
      min: minLength ?? Math.min(...allowedLengths),
      max: maxLength ?? Math.max(...allowedLengths),
      allowedLengths,
    };
  }

  if (minLength && maxLength) {
    return { min: minLength, max: maxLength, allowedLengths: null };
  }

  return getFallbackPhoneRule();
}

export function getPhoneRuleByCode(countryCode) {
  return getPhoneRuleFromCountry(getCountryByCode(countryCode));
}

/** e.g. min 8 max 8 → "8"; min 6 max 10 → "6–10" */
export function formatPhoneMinMaxLabel(min, max) {
  if (min === max) return String(min);
  return `${min}–${max}`;
}

/** Input placeholder: "Enter 8 digits" or "Enter 6/9 digits" / "Enter 6–10 digits". */
export function phoneLengthPlaceholder(rule) {
  if (!rule) return 'Enter digits';
  if (rule.allowedLengths?.length) {
    return `Enter ${rule.allowedLengths.join('/')} digits`;
  }
  return `Enter ${formatPhoneMinMaxLabel(rule.min, rule.max)} digits`;
}

/** Hint after country code: "8 digits" or "6, 9 digits" / "6–10 digits". */
export function phoneLengthHintSuffix(rule) {
  if (!rule) return '';
  if (rule.allowedLengths?.length) {
    return `${rule.allowedLengths.join(', ')} digits`;
  }
  return `${formatPhoneMinMaxLabel(rule.min, rule.max)} digits`;
}

/**
 * Validates national significant number (digits only, no country prefix).
 * Optional `mobileNationalPattern` on a country row restricts to mobile format.
 */
export function validateNationalPhoneDigits(phone, countryCode, { required = true } = {}) {
  const trimmed = String(phone || '').trim();
  if (!trimmed) {
    return required ? ['Phone number is required'] : [];
  }
  if (!/^\d+$/.test(trimmed)) {
    return ['Phone number must contain digits only'];
  }

  const country = getCountryByCode(countryCode);
  const rule = getPhoneRuleFromCountry(country);

  if (rule.allowedLengths && !rule.allowedLengths.includes(trimmed.length)) {
    return [
      `Phone number length for ${countryCode} must be one of: ${rule.allowedLengths.join(', ')} digits`,
    ];
  }

  if (trimmed.length < rule.min || trimmed.length > rule.max) {
    const lenLabel = formatPhoneMinMaxLabel(rule.min, rule.max);
    return [`Phone number must be ${lenLabel} digits`];
  }

  const pattern = country?.mobileNationalPattern;
  if (pattern && typeof pattern === 'string') {
    try {
      const re = new RegExp(pattern);
      if (!re.test(trimmed)) {
        return [
          country.mobileNationalPatternMessage
            || 'Enter a valid mobile number for this country.',
        ];
      }
    } catch {
      // invalid pattern in JSON — skip
    }
  }

  return [];
}

/** Bulk import: non-empty digits string; returns first error message or null. */
export function getBulkNationalPhoneValidationMessage(countryCode, digits) {
  const errs = validateNationalPhoneDigits(digits, countryCode, { required: true });
  return errs.length ? errs[0] : null;
}
